import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Check, Map as MapIcon, Trash2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Field, Select } from '@/components/ui/field';
import { Badge } from '@/components/ui/badge';
import { usePermission } from '@/Hooks/usePermission';
import { SpaceColumn } from './spaces/SpaceColumn';
import { SpaceDialog, type SpaceKind } from './spaces/SpaceDialog';
import { SpaceDeleteDialog, type SpaceDeleteTarget } from './spaces/SpaceDeleteDialog';
import { useSpacesChain } from './spaces/useSpacesChain';
import { areaDeleteBlock, floorDeleteBlock, listOf } from './spacesLib';
import type { Area, Floor, VenueTable } from './spacesLib';
import { api } from '@/lib/api';
import { resyncSpacesStructure } from '@/Redux/Slices/spacesApi';
import { useAppDispatch } from '@/Redux/store';

interface Editing {
  kind: SpaceKind;
  record: Floor | Area | VenueTable | null;
  parentId: string;
}

/**
 * Venue setup — the chain a venue needs before it can trade:
 * branch › floor › area › table.
 *
 * This screen exists because every other screen assumed these already existed.
 * The map editor places elements *onto* a floor, the QR sheet prints tokens for
 * tables that are already there — but nothing could create the floor or the
 * table, so a new tenant made a branch and stopped. §5.
 */
interface AreaCoverage {
  placed: number;
  total: number;
}

export default function SpacesPanel() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  /**
   * The whole hierarchy, its selection and its scope rule — shared with the
   * setup wizard's floor, area and table steps rather than written twice. No
   * `autoSelect` here: on this screen, picking a floor is how the operator says
   * which one the two columns beside it are showing, and choosing for them moves
   * those columns out from under them.
   */
  const {
    branches,
    branchId,
    setBranchId,
    floors,
    floorId,
    setFloorId,
    floorsState,
    loadFloors,
    areas,
    areaId,
    setAreaId,
    areasState,
    loadAreas,
    tables,
    tableId,
    setTableId,
    tablesState,
    loadTables,
    scopeBlock,
  } = useSpacesChain();

  // §38 — how much of this floor's operational reality is actually drawn. An
  // operator should be able to see "6 tables, 2 on the map" without opening the
  // map, because a table that is not on it can never show its occupancy.
  const [coverage, setCoverage] = useState<Record<string, AreaCoverage>>({});

  const [editing, setEditing] = useState<Editing | null>(null);
  const [deleting, setDeleting] = useState<SpaceDeleteTarget | null>(null);

  // ---------------------------------------------------------------- loading

  const loadCoverage = useCallback(() => {
    if (!floorId) {
      setCoverage({});
      return;
    }
    Promise.all([
      api.get('/floor-map', { params: { floorId } }),
      api.get('/tables', { params: { floorId, limit: 200 } }),
      api.get('/resources', { params: { floorId, limit: 200 } }),
    ])
      .then(([scenes, tableRows, resourceRows]) => {
        const placedIds = new Set(
          listOf<{ mapElements?: { entityId: string | null }[] }>(scenes.data).flatMap((scene) =>
            (scene.mapElements ?? [])
              .map((element) => element.entityId)
              .filter((id): id is string => !!id),
          ),
        );
        const rows = [
          ...listOf<{ id: string; areaId: string }>(tableRows.data),
          ...listOf<{ id: string; areaId: string }>(resourceRows.data),
        ];
        const next: Record<string, AreaCoverage> = {};
        for (const row of rows) {
          const cell = (next[row.areaId] ??= { placed: 0, total: 0 });
          cell.total += 1;
          if (placedIds.has(row.id)) cell.placed += 1;
        }
        setCoverage(next);
      })
      // Coverage is a hint, not the point of the screen: if it cannot be read,
      // the hierarchy still works.
      .catch(() => setCoverage({}));
  }, [floorId]);

  useEffect(loadCoverage, [loadCoverage]);

  // ---------------------------------------------------------------- actions

  /**
   * Every write on this screen goes through axios, so RTK Query hears nothing.
   * `spacesFloorMap` and `spacesTables` are both cached for an hour, and
   * `FloorMap` does not refetch its scenes on mount — so without this a floor
   * added here was simply absent from /floor-map until the entry expired.
   */
  const structureChanged = () => dispatch(resyncSpacesStructure());

  /** Ran only once the server has confirmed the delete, never on the click. */
  const removed = (target: SpaceDeleteTarget) => {
    if (target.kind === 'floor') {
      if (floorId === target.id) setFloorId(null);
      loadFloors();
    } else if (target.kind === 'area') {
      if (areaId === target.id) setAreaId(null);
      loadAreas();
    } else {
      if (tableId === target.id) setTableId(null);
      loadTables();
      // A deleted table is one fewer thing the map is missing.
      loadCoverage();
    }
    structureChanged();
  };

  // The two refusals a delete can meet, in the order the operator can act on:
  // the scope rule first, because no amount of emptying a floor gets round it.
  // Both helpers are shared with the setup wizard, which offers the same delete.
  const floorBlock = (floor: Floor): string | null =>
    scopeBlock('FLOOR', floor.id) ?? floorDeleteBlock(floor, t);

  const areaBlock = (area: Area): string | null =>
    scopeBlock('AREA', area.id) ?? areaDeleteBlock(area, t);

  const afterSave = () => {
    if (editing?.kind === 'floor') loadFloors();
    else if (editing?.kind === 'area') loadAreas();
    else loadTables();
    // A new table changes what is missing from the map, so the counts move too.
    loadCoverage();
    structureChanged();
  };

  // ------------------------------------------------------------ readiness

  const readiness = useMemo(() => {
    const steps = [
      { key: 'branch', done: branches.length > 0 },
      { key: 'floor', done: floors.length > 0 },
      { key: 'area', done: areas.length > 0 },
      { key: 'table', done: tables.length > 0 },
    ];
    return { steps, complete: steps.every((s) => s.done) };
  }, [branches.length, floors.length, areas.length, tables.length]);

  const floorCoverage = useMemo(() => {
    const areaIds = new Set(areas.map((a) => a.id));
    return Object.entries(coverage)
      .filter(([areaId]) => areaIds.has(areaId))
      .reduce(
        (total, [, cell]) => ({
          placed: total.placed + cell.placed,
          total: total.total + cell.total,
        }),
        { placed: 0, total: 0 },
      );
  }, [areas, coverage]);

  const selectedFloor = floors.find((f) => f.id === floorId) ?? null;
  const selectedArea = areas.find((a) => a.id === areaId) ?? null;
  const selectedTable = tables.find((tb) => tb.id === tableId) ?? null;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <p className="max-w-2xl text-sm text-ink-muted">{t('setup.intro')}</p>

        <Field label={t('setup.branch')}>
          <Select
            className="w-56"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            aria-label={t('setup.branch')}
          >
            {branches.length === 0 && <option value="">{t('setup.noBranches')}</option>}
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name} · {b.code}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {/* What a venue still needs before a guest can scan a table and order. */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-2 p-4">
          {readiness.steps.map((step, i) => (
            <div key={step.key} className="flex items-center gap-2 text-sm">
              <span
                className={
                  step.done
                    ? 'grid h-5 w-5 place-items-center rounded-full bg-green-100 text-green-700'
                    : 'grid h-5 w-5 place-items-center rounded-full bg-subtle text-ink-muted'
                }
              >
                {step.done ? <Check className="h-3 w-3" /> : i + 1}
              </span>
              <span className={step.done ? '' : 'text-ink-muted'}>
                {t(`setup.readiness.${step.key}`)}
              </span>
            </div>
          ))}
          <Badge tone={readiness.complete ? 'success' : 'warning'}>
            {t(readiness.complete ? 'setup.readiness.ready' : 'setup.readiness.incomplete')}
          </Badge>
        </CardContent>
      </Card>

      {/* Closes the loop the other way: setup can say what the map is missing,
          and hand you straight to the editor to fix it. */}
      {selectedFloor && floorCoverage.total > 0 && (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-3 p-4">
            <span className="text-sm font-medium">{selectedFloor.name}</span>
            <Badge tone={floorCoverage.placed >= floorCoverage.total ? 'success' : 'warning'}>
              {t('setup.map.coverage', {
                placed: floorCoverage.placed,
                total: floorCoverage.total,
              })}
            </Badge>
            {floorCoverage.placed < floorCoverage.total && (
              <span className="text-xs text-ink-muted">
                {t('setup.map.missingHint', {
                  count: floorCoverage.total - floorCoverage.placed,
                })}
              </span>
            )}
            {can('floor_map.view') && (
              <Button
                size="sm"
                variant={floorCoverage.placed < floorCoverage.total ? 'default' : 'outline'}
                className="ms-auto"
                onClick={() =>
                  navigate(
                    `/floor-map/${can('floor_map.update') ? 'edit' : 'live'}?floor=${
                      selectedFloor.id
                    }`,
                  )
                }
              >
                <MapIcon className="h-4 w-4" />
                {t('setup.map.open')}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col gap-4 lg:flex-row">
        <SpaceColumn
          title={t('setup.floors')}
          rows={floors.map((f) => ({
            id: f.id,
            title: f.name,
            subtitle: t('setup.floor.levelValue', { level: f.level }),
            meta: f._count ? t('setup.area.count', { count: f._count.areas }) : undefined,
            muted: !f.isActive,
            lockedReason: scopeBlock('FLOOR', f.id),
          }))}
          selectedId={floorId}
          onSelect={setFloorId}
          onCreate={() => setEditing({ kind: 'floor', record: null, parentId: branchId })}
          onEdit={(id) =>
            setEditing({
              kind: 'floor',
              record: floors.find((f) => f.id === id) ?? null,
              parentId: branchId,
            })
          }
          createLabel={t('setup.floor.add')}
          emptyTitle={t('setup.floor.emptyTitle')}
          emptyHint={t('setup.floor.emptyHint')}
          loading={floorsState.loading}
          error={floorsState.error}
          onRetry={loadFloors}
          canCreate={can('floor_map.create')}
          canEdit={can('floor_map.update')}
          // `POST /floors` needs a branchId, and the picker auto-selects the
          // first branch there is — so an empty one means the tenant has none,
          // not that nobody has chosen. Saying which beats a button that
          // disappears.
          createBlockedReason={branchId ? scopeBlock('BRANCH', branchId) : t('setup.noBranches')}
          footer={
            selectedFloor && can('floor_map.delete') ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={Boolean(floorBlock(selectedFloor))}
                title={floorBlock(selectedFloor) ?? undefined}
                onClick={() =>
                  setDeleting({ kind: 'floor', id: selectedFloor.id, label: selectedFloor.name })
                }
              >
                <Trash2 className="h-4 w-4" />
                {floorBlock(selectedFloor) ?? t('setup.floor.delete')}
              </Button>
            ) : undefined
          }
        />

        <SpaceColumn
          title={t('setup.areas')}
          waitingFor={floorId ? undefined : t('setup.area.pickFloor')}
          rows={areas.map((a) => {
            const cell = coverage[a.id];
            return {
              id: a.id,
              title: a.name,
              subtitle: t(`setup.area.types.${a.type}`, a.type),
              // The count that matters here is not capacity but how much of the
              // area is actually drawn on the map.
              meta: cell
                ? t('setup.area.onMap', { placed: cell.placed, total: cell.total })
                : a.capacity
                  ? t('setup.area.seats', { count: a.capacity })
                  : undefined,
              metaTone: cell
                ? cell.placed >= cell.total
                  ? ('success' as const)
                  : ('warning' as const)
                : undefined,
              muted: !a.isActive,
              lockedReason: scopeBlock('AREA', a.id),
            };
          })}
          selectedId={areaId}
          onSelect={setAreaId}
          onCreate={() => setEditing({ kind: 'area', record: null, parentId: floorId ?? '' })}
          onEdit={(id) =>
            setEditing({
              kind: 'area',
              record: areas.find((a) => a.id === id) ?? null,
              parentId: floorId ?? '',
            })
          }
          createLabel={t('setup.area.add')}
          emptyTitle={t('setup.area.emptyTitle')}
          emptyHint={t('setup.area.emptyHint')}
          loading={areasState.loading}
          error={areasState.error}
          onRetry={loadAreas}
          canCreate={can('floor_map.create')}
          canEdit={can('floor_map.update')}
          createBlockedReason={scopeBlock('FLOOR', floorId)}
          footer={
            selectedArea && can('floor_map.delete') ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={Boolean(areaBlock(selectedArea))}
                title={areaBlock(selectedArea) ?? undefined}
                onClick={() =>
                  setDeleting({ kind: 'area', id: selectedArea.id, label: selectedArea.name })
                }
              >
                <Trash2 className="h-4 w-4" />
                {areaBlock(selectedArea) ?? t('setup.area.delete')}
              </Button>
            ) : undefined
          }
        />

        <SpaceColumn
          title={t('setup.tables')}
          waitingFor={areaId ? undefined : t('setup.table.pickArea')}
          rows={tables.map((tb) => ({
            id: tb.id,
            title: tb.number,
            subtitle: tb.name ?? undefined,
            meta: t('setup.table.seatCount', { count: tb.seats }),
            muted: !tb.isActive,
            lockedReason: scopeBlock('TABLE', tb.id),
          }))}
          selectedId={tableId}
          onSelect={setTableId}
          onCreate={() => setEditing({ kind: 'table', record: null, parentId: areaId ?? '' })}
          onEdit={(id) =>
            setEditing({
              kind: 'table',
              record: tables.find((tb) => tb.id === id) ?? null,
              parentId: areaId ?? '',
            })
          }
          createLabel={t('setup.table.add')}
          emptyTitle={t('setup.table.emptyTitle')}
          emptyHint={t('setup.table.emptyHint')}
          loading={tablesState.loading}
          error={tablesState.error}
          onRetry={loadTables}
          canCreate={can('tables.create')}
          canEdit={can('tables.update')}
          createBlockedReason={scopeBlock('AREA', areaId)}
          footer={
            // `DELETE /tables/:id` exists behind `tables.delete` and this screen
            // never offered it, so the only way to remove a mistyped table was
            // the API. It has no server-side guard: sessions, reservations and
            // service requests are detached rather than blocking (`onDelete:
            // SetNull`), which is what the confirmation warns about.
            selectedTable && can('tables.delete') ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={Boolean(scopeBlock('TABLE', selectedTable.id))}
                title={scopeBlock('TABLE', selectedTable.id) ?? undefined}
                onClick={() =>
                  setDeleting({ kind: 'table', id: selectedTable.id, label: selectedTable.number })
                }
              >
                <Trash2 className="h-4 w-4" />
                {scopeBlock('TABLE', selectedTable.id) ?? t('setup.table.delete')}
              </Button>
            ) : undefined
          }
        />
      </div>

      {editing && (
        <SpaceDialog
          kind={editing.kind}
          open
          record={editing.record}
          parentId={editing.parentId}
          onClose={() => setEditing(null)}
          onSaved={afterSave}
        />
      )}

      <SpaceDeleteDialog
        target={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={removed}
      />
    </div>
  );
}
